import { registerPlugin } from "@capacitor/core";

import type { TorchPlugin } from "./definitions.js";

/// Capacitor plugin registration. Native bridges register
/// `Torch` automatically at app startup via the
/// `@CapacitorPlugin` annotation; the web fallback is loaded
/// lazily.
export const Torch = registerPlugin<TorchPlugin>("Torch", {
  web: () => import("./web.js").then((m) => new m.TorchWeb()),
});

export type {
  IsAvailableResult,
  SetOptions,
  TorchPlugin,
} from "./definitions.js";

/// Convenience wrapper that drives a Morse-style on/off timeline.
/// Each element of `timeline` is `[on, durationMs]`. The promise
/// resolves when the timeline completes (or when `cancelled` is
/// flipped to true by the caller).
export async function playTimeline(
  timeline: ReadonlyArray<readonly [boolean, number]>,
  cancelled: { current: boolean } = { current: false },
): Promise<void> {
  for (const [on, ms] of timeline) {
    if (cancelled.current) break;
    try {
      await Torch.set({ on });
    } catch {
      // If the platform refuses mid-sequence (camera taken by
      // another app etc.) bail rather than spam the rest of the
      // timeline.
      break;
    }
    await sleep(ms);
  }
  // Ensure we don't leave the torch latched on at the end.
  try {
    await Torch.set({ on: false });
  } catch {
    /* already off / unavailable — ignore */
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
